import { Stack } from "@mui/material";
import { useEffect, useState } from "react";
import { assetService } from "../../../../shared/services/AssetService";
import { mathCalculationService } from "../../../../shared/services/MathCalculationService";
import {
    usePortfolioSelection,
} from "../../../../shared/contexts/portfolio-context/components/PortfolioContextProvider";
import { ChangingDirectionType } from "../../types";
import { SaveCustomImpliedVolatilityButton } from "../SaveCustomImpliedVolatilityButton";
import { StepChangeSelector } from "../StepChangeSelector";
import { PositionImpliedVolatilityChangingParameters } from "./PositionImpliedVolatilityChangingParameters";
import { CustomImpliedVolatilityToggleButton } from "../../views/CustomImpliedVolatilityToggleButton";

const defaultStepValue = 1;

export function PositionImpliedVolatilitySelector({
    parameters,
}: {
    parameters: PositionImpliedVolatilityChangingParameters;
}) {
    const {
        selectedPortfolio,
        isAllPortfoliosSelected,
    } = usePortfolioSelection();

    const isCustomImpliedVolatilityEnabled = selectedPortfolio !== null && selectedPortfolio.portfolioSettings !== null
        ? selectedPortfolio.portfolioSettings.isCustomImpliedVolatilityEnabled
        : false;

    const [stepValue, setStepValue] = useState<number>(
        parameters.selectedPosition?.customImpliedVolatilityStep ?? defaultStepValue
    );

    useEffect(() => {
        setStepValue(parameters.selectedPosition?.customImpliedVolatilityStep ?? defaultStepValue);
    }, [parameters.selectedPosition]);

    const isApplicable =
        isCustomImpliedVolatilityEnabled &&
        parameters.selectedPosition !== null &&
        assetService.isOptionType(parameters.selectedPosition.type) &&
        parameters.selectedPosition.platformAllocations.some(allocation =>
            mathCalculationService.round(allocation.currentSize, 6) !== 0) &&
        parameters.selectedPosition.expirationPrice === null;

    const handleChange = async (direction: ChangingDirectionType, step: number): Promise<void> => {
        const selectedPosition = parameters.selectedPosition;

        if (selectedPosition === null || isAllPortfoliosSelected) {
            return;
        }

        const positions = parameters.positions.filter(x =>
            x.symbol === selectedPosition.symbol &&
            x.type === selectedPosition.type &&
            selectedPosition.platformAllocations.some(allocation =>
                allocation.targetPlatform === x.targetPlatform));

        if (positions.length === 0 || positions.some(position => position.customImpliedVolatility === null))
            return;

        const directionMultiplier = direction === ChangingDirectionType.Next ? 1 : -1;
        const updates = positions.map(position => ({
            targetPlatform: position.targetPlatform,
            customImpliedVolatility: mathCalculationService.round(
                position.customImpliedVolatility! + directionMultiplier * step / 100,
                4,
            ),
            customImpliedVolatilityStep: step,
        }));

        await parameters.updatePositionsInSelectedPortfolioWrapper(selectedPosition.symbol, updates);
    }

    const handleStepSelectionChange = async (step: number): Promise<void> => {
        const selectedPosition = parameters.selectedPosition;

        if (selectedPosition === null || selectedPosition.customImpliedVolatility == null || isAllPortfoliosSelected) {
            return;
        }

        const updates = parameters.positions
            .filter(position =>
                position.symbol === selectedPosition.symbol &&
                position.type === selectedPosition.type &&
                position.customImpliedVolatility !== null &&
                selectedPosition.platformAllocations.some(allocation =>
                    allocation.targetPlatform === position.targetPlatform))
            .map(position => ({
                targetPlatform: position.targetPlatform,
                customImpliedVolatility: position.customImpliedVolatility!,
                customImpliedVolatilityStep: step,
            }));

        await parameters.updatePositionsInSelectedPortfolioWrapper(selectedPosition.symbol, updates);
    }

    return (
        <Stack direction="row" spacing={0.5}>
            <CustomImpliedVolatilityToggleButton />
            <StepChangeSelector
                selectedStep={stepValue}
                setSelectedStep={setStepValue}
                stepOptions={[10, 5, 1, 0.5, 0.1, 0.05, 0.01]}
                disabled={isAllPortfoliosSelected || !isApplicable}
                buttonContent="V"
                previousTooltipText="Reduce implied volatility"
                nextTooltipText="Increase implied volatility"
                onChange={handleChange}
                onStepSelectionChange={handleStepSelectionChange}
                selectorWidth={65}
            />
            <SaveCustomImpliedVolatilityButton />
        </Stack>
    );
}
